import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UploadProps } from 'antd';
import { Alert, App, Checkbox, Form, Input, Modal, Select, Space, Tabs, Tag, Upload } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../context/AuthProvider';
import type { ControllerResponse } from '../../DTOS/Controller/ControllerResponse';
import type {
  BulkRunNotificationsResult,
  ClientInvoiceSummary,
  Dashboard,
  FacturiScadenteScheduleStatus,
  InvoiceGroupRow,
  LatestImportStatus,
  NotificationCategorySetting,
  NotificationClientHistory,
  NotificationClientPreview,
  NotificationPreflightStatus,
  NotificationRunStatus,
  RunNotificationsResult,
  SagaCompany,
  ValidationError,
} from '../../DTOS/FacturiScadente';
import FacturiScadenteService from '../../services/facturiScadente/FacturiScadenteService';
import ClientiTab from './ClientiTab';
import DashboardTab from './DashboardTab';
import { ALLOWED_IMPORT_EXTENSIONS, ALLOWED_IMPORT_EXTENSIONS_TEXT, isAllowedImportFile } from './importFileTypes';
import NotificationConfigTab from './NotificationConfigTab';
import NotificationTab from './NotificationTab';

const toNumber = (value: unknown): number => {
  if (value == null) {
    return 0;
  }
  if (typeof value === 'number') {
    return value;
  }
  const cleaned = String(value).replace(/\\s/g, '').replace(/\\./g, '').replace(',', '.');
  const parsed = Number(cleaned);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const attachPdfModeOptions = [
  { label: 'La fiecare trimitere', value: 'always' },
  { label: 'O singura data', value: 'once' },
];
const categoryOptions = [
  { label: 'B2G', value: 'B2G' },
  { label: 'B2B', value: 'B2B' },
  { label: 'B2C', value: 'B2C' },
  { label: 'fara categorie', value: 'fara categorie' },
];
const registrationNumberRegex =
  /^(?:[JFC]\s*(?:0[1-9]|[1-3]\d|40|51|52)\s*\/\s*\d{1,6}\s*\/\s*(?:19|20)\d{2}|[JFC](?:19|20)\d{2}\d{6}(?:00|0[1-9]|[1-3]\d|40|51|52)\d)$/i;
const normalizeCuiKey = (value?: string): string => (value ? value.replace(/\D/g, '') : '');

const FacturiScadente: React.FC = (): JSX.Element => {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const { profile } = useAuth();

  const isAdmin = profile?.userPermission?.rols?.includes('admin');
  const isAccountant = profile?.userPermission?.rols?.includes('accountant');
  const canManage = isAdmin || isAccountant;

  const [activeTab, setActiveTab] = useState<string>('dashboard');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [emailFilterMode, setEmailFilterMode] = useState<'' | 'with' | 'without'>('');
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(20);
  const [hasOutstandingOnly, setHasOutstandingOnly] = useState<boolean>(false);
  const [notificationsEnabledOnly, setNotificationsEnabledOnly] = useState<boolean>(false);
  const [historyDateRange, setHistoryDateRange] = useState<[string | null, string | null]>([null, null]);
  const [historyClientName, setHistoryClientName] = useState<string>('');
  const [historyCui, setHistoryCui] = useState<string>('');
  const [historyPage, setHistoryPage] = useState<number>(1);
  const [historyPageSize, setHistoryPageSize] = useState<number>(20);
  const [editModalOpen, setEditModalOpen] = useState<boolean>(false);
  const [editingClient, setEditingClient] = useState<SagaCompany | null>(null);
  const [autoEditClientId, setAutoEditClientId] = useState<string | null>(null);
  const [bulkModalOpen, setBulkModalOpen] = useState<boolean>(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [isPreviewingClientNotificationPdfId, setIsPreviewingClientNotificationPdfId] = useState<string | null>(null);
  const [bulkForm] = Form.useForm();
  const [form] = Form.useForm();
  const notificationsEnabled = Form.useWatch('isEmailNotificationsEnabled', form);
  const editAttachPaymentNotificationPdf = Form.useWatch('attachPaymentNotificationPdf', form);
  const bulkFormValues = Form.useWatch([], bulkForm) as Record<string, unknown> | undefined;
  const bulkNotificationsEnabled = bulkFormValues?.isEmailNotificationsEnabled;
  const bulkAttachPaymentNotificationPdf = bulkFormValues?.attachPaymentNotificationPdf;

  const {
    data: dashboardData,
    isFetching: isDashboardLoading,
    refetch: refetchDashboard,
  } = useQuery({
    queryKey: ['facturiScadenteDashboard'],
    queryFn: () =>
      FacturiScadenteService.dashboard().then((data: ControllerResponse<Dashboard>) => data?.response as Dashboard),
    refetchOnWindowFocus: false,
  });

  const {
    data: latestImportData,
    isFetching: isLatestImportLoading,
    refetch: refetchLatestImport,
  } = useQuery({
    queryKey: ['facturiScadenteLatestImport'],
    queryFn: () =>
      FacturiScadenteService.latestImport().then(
        (data: ControllerResponse<LatestImportStatus>) => data?.response as LatestImportStatus,
      ),
    refetchOnWindowFocus: false,
  });

  const {
    data: notificationStatusData,
    isFetching: isNotificationStatusLoading,
    refetch: refetchNotificationStatus,
  } = useQuery({
    queryKey: ['facturiScadenteNotificationStatus'],
    queryFn: () =>
      FacturiScadenteService.notificationStatus().then(
        (data: ControllerResponse<NotificationRunStatus>) => data?.response as NotificationRunStatus,
      ),
    refetchOnWindowFocus: false,
  });

  const {
    data: preflightData,
    isFetching: isPreflightLoading,
    refetch: refetchPreflight,
  } = useQuery({
    queryKey: ['facturiScadenteNotificationPreflight'],
    queryFn: () =>
      FacturiScadenteService.notificationPreflight().then(
        (data: ControllerResponse<NotificationPreflightStatus>) => data?.response as NotificationPreflightStatus,
      ),
    refetchOnWindowFocus: false,
  });

  const {
    data: categorySettingsResponse,
    isFetching: isCategorySettingsLoading,
    refetch: refetchCategorySettings,
  } = useQuery({
    queryKey: ['facturiScadenteNotificationCategories'],
    queryFn: () =>
      FacturiScadenteService.getNotificationCategories().then(
        (data: ControllerResponse<NotificationCategorySetting[]>) => data,
      ),
    refetchOnWindowFocus: false,
  });

  const {
    data: clientsResponse,
    isFetching: isClientsLoading,
    isError: isClientsError,
    error: clientsError,
    refetch: refetchClients,
  } = useQuery({
    queryKey: [
      'facturiScadenteClients',
      searchTerm,
      categoryFilter,
      emailFilterMode,
      hasOutstandingOnly,
      notificationsEnabledOnly,
      page,
      pageSize,
    ],
    queryFn: () =>
      FacturiScadenteService.getClients({
        search: searchTerm || undefined,
        category: categoryFilter || undefined,
        missingEmailOnly: emailFilterMode === 'without',
        withEmailOnly: emailFilterMode === 'with',
        hasOutstandingOnly,
        notificationsEnabledOnly,
        skip: (page - 1) * pageSize,
        take: pageSize,
      }).then((data: ControllerResponse<SagaCompany[]>) => data),
    refetchOnWindowFocus: false,
  });

  const {
    data: clientSummaryResponse,
    isFetching: isClientSummaryLoading,
    refetch: refetchClientSummary,
  } = useQuery({
    queryKey: [
      'facturiScadenteClientSummary',
      searchTerm,
      categoryFilter,
      emailFilterMode,
      hasOutstandingOnly,
      notificationsEnabledOnly,
    ],
    queryFn: () =>
      FacturiScadenteService.getClientSummary({
        search: searchTerm || undefined,
        category: categoryFilter || undefined,
        missingEmailOnly: emailFilterMode === 'without',
        withEmailOnly: emailFilterMode === 'with',
        hasOutstandingOnly,
        notificationsEnabledOnly,
      }).then((data: ControllerResponse<ClientInvoiceSummary[]>) => data),
    refetchOnWindowFocus: false,
  });

  const {
    data: configClientsResponse,
    isFetching: isConfigClientsLoading,
    isError: isConfigClientsError,
    error: configClientsError,
    refetch: refetchConfigClients,
  } = useQuery({
    queryKey: ['facturiScadenteConfigClients'],
    queryFn: () => FacturiScadenteService.getClients({}).then((data: ControllerResponse<SagaCompany[]>) => data),
    refetchOnWindowFocus: false,
  });

  const {
    data: configInvoicesResponse,
    isFetching: isConfigInvoicesLoading,
    isError: isConfigInvoicesError,
    error: configInvoicesError,
    refetch: refetchConfigInvoices,
  } = useQuery({
    queryKey: ['facturiScadenteConfigInvoices'],
    queryFn: () =>
      FacturiScadenteService.groupedInvoices({
        overdueOnly: false,
      }).then((data: ControllerResponse<InvoiceGroupRow[]>) => data),
    refetchOnWindowFocus: false,
  });

  const {
    data: scheduleData,
    isFetching: isScheduleLoading,
    refetch: refetchSchedule,
  } = useQuery({
    queryKey: ['facturiScadenteSchedule'],
    queryFn: () =>
      FacturiScadenteService.scheduleStatus().then(
        (data: ControllerResponse<FacturiScadenteScheduleStatus>) => data?.response as FacturiScadenteScheduleStatus,
      ),
    refetchOnWindowFocus: false,
  });

  const {
    data: historyResponse,
    isFetching: isHistoryLoading,
    isError: isHistoryError,
    error: historyError,
    refetch: refetchHistory,
  } = useQuery({
    queryKey: [
      'facturiScadenteNotificationHistory',
      historyDateRange,
      historyClientName,
      historyCui,
      historyPage,
      historyPageSize,
    ],
    queryFn: () =>
      FacturiScadenteService.notificationHistory({
        dateFrom: historyDateRange[0] || undefined,
        dateTo: historyDateRange[1] || undefined,
        clientName: historyClientName || undefined,
        cui: historyCui || undefined,
        skip: (historyPage - 1) * historyPageSize,
        take: historyPageSize,
      }).then((data: ControllerResponse<NotificationClientHistory[]>) => data),
    refetchOnWindowFocus: false,
  });

  const importInvoicesMutation = useMutation({
    mutationFn: async (file: File) => {
      const response = await FacturiScadenteService.importInvoices(file);
      return (await response.json()) as ControllerResponse<any>;
    },
    onSuccess: (data: ControllerResponse<any>) => {
      if (data?.errors && data.errors.length > 0) {
        message.error(data.errors[0]);
        return;
      }
      message.success('Import facturi realizat.');
      refetchLatestImport();
      refetchNotificationStatus();
      refetchDashboard();
      refetchClients();
      refetchConfigClients();
      refetchConfigInvoices();
      refetchPreflight();
    },
    onError: (error: any) => {
      if (String(error?.message).includes('409')) {
        message.error('Fisierul a fost deja importat.');
        return;
      }
      message.error(error?.message || 'Eroare la import.');
    },
  });

  const importCompaniesMutation = useMutation({
    mutationFn: async (file: File) => {
      const response = await FacturiScadenteService.importCompanies(file);
      return (await response.json()) as ControllerResponse<any>;
    },
    onSuccess: (data: ControllerResponse<any>) => {
      if (data?.errors && data.errors.length > 0) {
        message.error(data.errors[0]);
        return;
      }
      message.success('Import clienti realizat.');
      refetchClients();
      refetchLatestImport();
      refetchNotificationStatus();
      refetchDashboard();
      refetchPreflight();
      refetchCategorySettings();
      refetchConfigClients();
    },
    onError: (error: any) => {
      if (String(error?.message).includes('409')) {
        message.error('Fisierul a fost deja importat.');
        return;
      }
      message.error(error?.message || 'Eroare la import.');
    },
  });

  const runNowMutation = useMutation({
    mutationFn: () => FacturiScadenteService.runNotifications(),
    onSuccess: (data: ControllerResponse<RunNotificationsResult>) => {
      const result = data?.response;
      if (result?.isBlocked) {
        message.warning(result.blockedReason || 'Notificarile sunt blocate.');
      } else {
        message.success(`Notificari trimise: ${result?.sentCount ?? 0}, esuate: ${result?.failedCount ?? 0}.`);
      }
      refetchNotificationStatus();
      refetchLatestImport();
      refetchPreflight();
      refetchHistory();
    },
    onError: (error: any) => {
      message.error(error?.message || 'Eroare la rularea notificarilor.');
    },
  });

  const runClientNotificationMutation = useMutation({
    mutationFn: (clientId: string) => FacturiScadenteService.runNotificationsForClient(clientId),
    onSuccess: (data: ControllerResponse<RunNotificationsResult>) => {
      const result = data?.response;
      if (result?.isBlocked) {
        message.warning(result.blockedReason || 'Notificarea nu poate fi trimisa pentru client.');
      } else {
        message.success(`Notificari trimise: ${result?.sentCount ?? 0}, esuate: ${result?.failedCount ?? 0}.`);
      }

      refetchNotificationStatus();
      refetchLatestImport();
      refetchHistory();
      refetchDashboard();
      refetchClientSummary();
      refetchPreflight();
    },
    onError: (error: any) => {
      message.error(error?.message || 'Eroare la trimiterea notificarii pentru client.');
    },
  });

  const previewClientNotificationMutation = useMutation({
    mutationFn: (clientId: string) => FacturiScadenteService.previewNotificationForClient(clientId),
    onSuccess: (data: ControllerResponse<NotificationClientPreview>) => {
      const preview = data?.response;
      if (!preview) {
        message.warning('Nu s-a putut genera preview-ul notificarii.');
        return;
      }

      const title = `Preview notificare - ${preview.clientName || preview.customerCui || 'client'}`;
      const content = (
        <div>
          <div>
            <strong>Status:</strong>{' '}
            {preview.isBlocked ? `Blocat - ${preview.blockedReason || 'Motiv necunoscut'}` : 'Ready pentru trimitere'}
          </div>
          <div>
            <strong>Subiect:</strong> {preview.subject || '-'}
          </div>
          <div>
            <strong>To:</strong> {preview.toRecipients?.join(', ') || '-'}
          </div>
          <div>
            <strong>BCC:</strong> {preview.ccRecipients?.join(', ') || '-'}
          </div>
          <div>
            <strong>Facturi:</strong> {preview.invoiceCount ?? 0}
          </div>
          <div>
            <strong>Total:</strong> {preview.totals || '-'}
          </div>
          <div>
            <strong>PDF notificare:</strong> {preview.attachPaymentNotificationPdf ? 'Da' : 'Nu'}
          </div>
          <div>
            <strong>Mod PDF:</strong>{' '}
            {preview.attachPaymentNotificationPdfMode === 'once' ? 'O singura data' : 'La fiecare trimitere'}
          </div>
        </div>
      );

      if (preview.isBlocked) {
        Modal.info({
          title,
          width: 760,
          okText: 'Inchide',
          content,
        });
        return;
      }

      Modal.confirm({
        title,
        width: 760,
        okText: 'Trimite',
        cancelText: 'Anuleaza',
        content,
        onOk: () => runClientNotificationMutation.mutate(preview.clientId),
      });
    },
    onError: (error: any) => {
      message.error(error?.message || 'Eroare la generarea preview-ului.');
    },
  });

  const runBulkClientNotificationMutation = useMutation({
    mutationFn: (clientIds: string[]) => FacturiScadenteService.runNotificationsForClients(clientIds),
    onSuccess: (data: ControllerResponse<BulkRunNotificationsResult>) => {
      if (data?.errors && data.errors.length > 0) {
        message.error(data.errors[0]);
        return;
      }

      const summary = data?.response;
      if (!summary) {
        message.warning('Nu s-a putut calcula rezumatul pentru trimiterea in grup.');
        return;
      }

      message.success(
        `Notificari in grup finalizate. Procesate: ${summary.processedClients}/${summary.requestedClients}, trimise: ${summary.sentCount}, esuate: ${summary.failedCount}, blocate: ${summary.blockedClients}, erori: ${summary.errorClients}.`,
      );
      refetchNotificationStatus();
      refetchLatestImport();
      refetchHistory();
      refetchDashboard();
      refetchClientSummary();
      refetchPreflight();
    },
    onError: (error: any) => {
      message.error(error?.message || 'Eroare la trimiterea notificarilor in grup.');
    },
  });

  const approveImportMutation = useMutation({
    mutationFn: () => FacturiScadenteService.approveLatestImport(),
    onSuccess: (data: ControllerResponse<LatestImportStatus>) => {
      if (data?.errors && data.errors.length > 0) {
        message.error(data.errors[0]);
        return;
      }
      message.success('Import aprobat. Notificarile pot fi trimise.');
      refetchLatestImport();
      refetchNotificationStatus();
    },
    onError: (error: any) => {
      message.error(error?.message || 'Eroare la aprobare.');
    },
  });

  const updateClientMutation = useMutation({
    mutationFn: (payload: any) => {
      if (!editingClient) {
        return Promise.resolve(null);
      }
      return FacturiScadenteService.patchClient(editingClient.id, payload);
    },
    onSuccess: () => {
      message.success('Client actualizat.');
      setEditModalOpen(false);
      setEditingClient(null);
      queryClient.invalidateQueries({ queryKey: ['facturiScadenteClients'] });
      refetchClients();
      refetchClientSummary();
      refetchConfigClients();
      refetchLatestImport();
      refetchNotificationStatus();
      refetchDashboard();
      refetchPreflight();
    },
    onError: (error: any) => {
      message.error(error?.message || 'Eroare la salvare.');
    },
  });

  const bulkUpdateMutation = useMutation({
    mutationFn: (payload: any) => FacturiScadenteService.bulkPatchClients(payload),
    onSuccess: (data: ControllerResponse<number>) => {
      if (data?.errors && data.errors.length > 0) {
        message.error(data.errors[0]);
        return;
      }
      message.success(`Actualizat ${data?.response ?? 0} clienti.`);
      setBulkModalOpen(false);
      bulkForm.resetFields();
      setSelectedRowKeys([]);
      queryClient.invalidateQueries({ queryKey: ['facturiScadenteClients'] });
      refetchClients();
      refetchClientSummary();
      refetchConfigClients();
      refetchLatestImport();
      refetchNotificationStatus();
      refetchDashboard();
      refetchPreflight();
    },
    onError: (error: any) => {
      message.error(error?.message || 'Eroare la salvare.');
    },
  });

  const updateCategorySettingsMutation = useMutation({
    mutationFn: (categories: NotificationCategorySetting[]) =>
      FacturiScadenteService.updateNotificationCategories(categories),
    onSuccess: () => {
      message.success('Categorii actualizate.');
      refetchCategorySettings();
      refetchConfigClients();
      refetchPreflight();
    },
    onError: (error: any) => {
      message.error(error?.message || 'Eroare la actualizarea categoriilor.');
    },
  });

  const saveNotificationConfigMutation = useMutation({
    mutationFn: (payload: {
      categories: { category: string; isEnabled: boolean }[];
      clients: {
        clientId: string;
        email?: string;
        billingEmail?: string;
        category?: string;
        isEmailNotificationsEnabled?: boolean;
        attachPaymentNotificationPdf?: boolean;
        attachPaymentNotificationPdfMode?: 'always' | 'once';
      }[];
      invoices: {
        invoiceId: string;
        isNotificationEnabled?: boolean;
      }[];
    }) => FacturiScadenteService.updateNotificationConfig(payload),
    onSuccess: () => {
      message.success('Configurarile au fost salvate.');
      refetchCategorySettings();
      refetchClients();
      refetchClientSummary();
      refetchConfigClients();
      refetchConfigInvoices();
      refetchPreflight();
    },
    onError: (error: any) => {
      message.error(error?.message || 'Eroare la salvarea configurarilor.');
    },
  });

  useEffect(() => {
    if (!autoEditClientId || !clientsResponse?.response) {
      return;
    }
    const match = clientsResponse.response.find((client) => client.id === autoEditClientId);
    if (match) {
      setAutoEditClientId(null);
      openEditModal(match);
    }
  }, [autoEditClientId, clientsResponse]);

  const openEditModal = (client: SagaCompany) => {
    setEditingClient(client);
    form.setFieldsValue({
      name: client.name,
      category: client.category ?? 'fara categorie',
      registrationNumber: client.registrationNumber,
      email: client.email,
      billingEmail: client.billingEmail,
      phone: client.phone,
      isEmailNotificationsEnabled: client.isEmailNotificationsEnabled ?? true,
      needsInfo: client.needsInfo ?? false,
      attachPaymentNotificationPdf: client.attachPaymentNotificationPdf ?? false,
      attachPaymentNotificationPdfMode: client.attachPaymentNotificationPdfMode ?? 'always',
    });
    setEditModalOpen(true);
  };

  const closeEditModal = () => {
    setEditModalOpen(false);
    setEditingClient(null);
    form.resetFields();
  };

  const handleSaveClient = async () => {
    try {
      const values = await form.validateFields();
      updateClientMutation.mutate(values);
    } catch {
      // form validation errors
    }
  };

  const handleEditFromError = (error: ValidationError) => {
    setActiveTab('clients');
    setEmailFilterMode('without');
    setPage(1);
    const term = error.clientName || error.clientCode || '';
    setSearchTerm(term);
    if (error.clientId) {
      setAutoEditClientId(error.clientId);
    }
  };

  const handlePreviewNotificationForClient = (client: SagaCompany) => {
    if (!client?.id) {
      return;
    }

    previewClientNotificationMutation.mutate(client.id);
  };

  const handlePreviewNotificationPdfForClient = async (client: SagaCompany) => {
    if (!client?.id) {
      return;
    }

    try {
      setIsPreviewingClientNotificationPdfId(client.id);
      const response = (await FacturiScadenteService.previewNotificationPdfForClient(client.id)) as Response;
      const pdfBlob = await response.blob();
      const objectUrl = URL.createObjectURL(pdfBlob);
      const openedWindow = window.open(objectUrl, '_blank', 'noopener,noreferrer');
      if (!openedWindow) {
        message.warning('Browser-ul a blocat tab-ul de preview PDF.');
      }
      setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
    } catch {
      message.error('Nu s-a putut genera preview PDF (verifica eligibilitatea clientului).');
    } finally {
      setIsPreviewingClientNotificationPdfId(null);
    }
  };

  const handleToggleCategory = (category: string, isEnabled: boolean) => {
    const current = (categorySettingsResponse?.response ?? []) as NotificationCategorySetting[];
    const next = current.map((item) => (item.category === category ? { ...item, isEnabled } : item));
    updateCategorySettingsMutation.mutate(next);
  };

  const handleRunBulkNotification = () => {
    const ids = selectedRowKeys.map((key) => String(key)).filter((id) => !!id);
    if (ids.length === 0) {
      message.warning('Selecteaza cel putin un client.');
      return;
    }

    Modal.confirm({
      title: 'Trimite notificari in grup',
      content: `Vrei sa trimiti notificari pentru ${ids.length} clienti selectati?`,
      okText: 'Trimite',
      cancelText: 'Renunta',
      onOk: () => runBulkClientNotificationMutation.mutate(ids),
    });
  };

  const refreshAll = () => {
    refetchDashboard();
    refetchLatestImport();
    refetchNotificationStatus();
    refetchPreflight();
    refetchCategorySettings();
    refetchSchedule();
    refetchClients();
    refetchClientSummary();
    refetchConfigClients();
    refetchConfigInvoices();
    refetchHistory();
  };

  const handleBulkSave = async () => {
    try {
      const values = await bulkForm.validateFields();
      const payload: Record<string, unknown> = {
        ids: selectedRowKeys,
      };

      if (typeof values.email === 'string' && values.email.trim() !== '') {
        payload.email = values.email.trim();
      }
      if (typeof values.billingEmail === 'string' && values.billingEmail.trim() !== '') {
        payload.billingEmail = values.billingEmail.trim();
      }
      if (typeof values.phone === 'string' && values.phone.trim() !== '') {
        payload.phone = values.phone.trim();
      }
      if (typeof values.category === 'string' && values.category.trim() !== '') {
        payload.category = values.category.trim();
      }
      if (typeof values.isEmailNotificationsEnabled === 'boolean') {
        payload.isEmailNotificationsEnabled = values.isEmailNotificationsEnabled;
      }
      if (typeof values.needsInfo === 'boolean') {
        payload.needsInfo = values.needsInfo;
      }
      if (typeof values.attachPaymentNotificationPdf === 'boolean') {
        payload.attachPaymentNotificationPdf = values.attachPaymentNotificationPdf;
      }
      if (
        typeof values.attachPaymentNotificationPdfMode === 'string' &&
        values.attachPaymentNotificationPdfMode.trim() !== ''
      ) {
        payload.attachPaymentNotificationPdfMode = values.attachPaymentNotificationPdfMode;
      }

      if (Object.keys(payload).length === 1) {
        message.warning('Nu ai setat niciun camp pentru modificare.');
        return;
      }

      bulkUpdateMutation.mutate({
        ...payload,
      });
    } catch {
      // form validation errors
    }
  };

  const bulkChangedFields = useMemo(() => {
    const values = bulkFormValues ?? {};
    const changed: { key: string; label: string }[] = [];

    if (typeof values.email === 'string' && values.email.trim() !== '') {
      changed.push({ key: 'email', label: 'Email' });
    }
    if (typeof values.billingEmail === 'string' && values.billingEmail.trim() !== '') {
      changed.push({ key: 'billingEmail', label: 'Email facturi' });
    }
    if (typeof values.phone === 'string' && values.phone.trim() !== '') {
      changed.push({ key: 'phone', label: 'Telefon' });
    }
    if (typeof values.category === 'string' && values.category.trim() !== '') {
      changed.push({ key: 'category', label: 'Categorie' });
    }
    if (typeof values.isEmailNotificationsEnabled === 'boolean') {
      changed.push({ key: 'isEmailNotificationsEnabled', label: 'Trimite notificari' });
    }
    if (typeof values.needsInfo === 'boolean') {
      changed.push({ key: 'needsInfo', label: 'Necesita info' });
    }
    if (typeof values.attachPaymentNotificationPdf === 'boolean') {
      changed.push({ key: 'attachPaymentNotificationPdf', label: 'Ataseaza PDF notificare' });
    }
    if (
      typeof values.attachPaymentNotificationPdfMode === 'string' &&
      values.attachPaymentNotificationPdfMode.trim() !== ''
    ) {
      changed.push({ key: 'attachPaymentNotificationPdfMode', label: 'Mod trimitere PDF' });
    }

    return changed;
  }, [bulkFormValues]);

  const invoiceUploadProps: UploadProps = {
    accept: ALLOWED_IMPORT_EXTENSIONS.join(','),
    maxCount: 1,
    showUploadList: false,
    beforeUpload: (file) => {
      if (!isAllowedImportFile(file.name)) {
        message.error(`Incarca un fisier valid (${ALLOWED_IMPORT_EXTENSIONS_TEXT}).`);
        return Upload.LIST_IGNORE;
      }
      importInvoicesMutation.mutate(file as File);
      return false;
    },
  };

  const companyUploadProps: UploadProps = {
    accept: ALLOWED_IMPORT_EXTENSIONS.join(','),
    maxCount: 1,
    showUploadList: false,
    beforeUpload: (file) => {
      if (!isAllowedImportFile(file.name)) {
        message.error(`Incarca un fisier valid (${ALLOWED_IMPORT_EXTENSIONS_TEXT}).`);
        return Upload.LIST_IGNORE;
      }
      importCompaniesMutation.mutate(file as File);
      return false;
    },
  };

  const validationErrors = latestImportData?.validationErrors ?? [];

  const clientsPayload = clientsResponse as unknown as ControllerResponse<SagaCompany[]> | SagaCompany[] | null;
  const clients = Array.isArray(clientsPayload) ? clientsPayload : (clientsPayload?.response ?? []);
  const totalItems = Array.isArray(clientsPayload)
    ? clientsPayload.length
    : (clientsPayload?.totalItems ?? clients.length);

  const configClientsPayload = configClientsResponse as unknown as
    | ControllerResponse<SagaCompany[]>
    | SagaCompany[]
    | null;
  const configClients = Array.isArray(configClientsPayload)
    ? configClientsPayload
    : (configClientsPayload?.response ?? []);

  const clientSummaryPayload = clientSummaryResponse as unknown as ControllerResponse<ClientInvoiceSummary[]> | null;
  const clientSummary = clientSummaryPayload?.response ?? [];
  const summaryByCui = useMemo(() => {
    const map = new Map<string, ClientInvoiceSummary>();
    clientSummary.forEach((item) => {
      if (item.customerCui) {
        map.set(normalizeCuiKey(item.customerCui), item);
      }
    });
    return map;
  }, [clientSummary]);

  const categorySettings = useMemo(() => {
    const categories = (categorySettingsResponse?.response ?? []) as NotificationCategorySetting[];
    const order = new Map<string, number>([
      ['B2G', 1],
      ['B2B', 2],
      ['B2C', 3],
      ['fara categorie', 4],
    ]);
    return [...categories].sort((a, b) => (order.get(a.category) ?? 99) - (order.get(b.category) ?? 99));
  }, [categorySettingsResponse]);

  const configInvoiceRows = ((configInvoicesResponse as any)?.response ?? []) as InvoiceGroupRow[];
  const historyRows = (historyResponse as any)?.response ?? [];
  const historyTotalItems = (historyResponse as any)?.totalItems ?? 0;
  const configLoading = isConfigClientsLoading || isConfigInvoicesLoading;
  const configErrorMessage = (configClientsError as any)?.message || (configInvoicesError as any)?.message;

  return (
    <div className='table-page-no-h-scroll'>
      <h1 style={{ marginBottom: 24 }}>Facturi Scadente</h1>
      <Tabs
        activeKey={activeTab}
        onChange={(key) => setActiveTab(key)}
        items={[
          {
            key: 'dashboard',
            label: 'Overview',
            children: (
              <DashboardTab
                dashboardData={dashboardData}
                latestImportData={latestImportData}
                notificationStatusData={notificationStatusData}
                preflightData={preflightData}
                scheduleData={scheduleData}
                validationErrors={validationErrors}
                isDashboardLoading={isDashboardLoading}
                isLatestImportLoading={isLatestImportLoading}
                isNotificationStatusLoading={isNotificationStatusLoading}
                isPreflightLoading={isPreflightLoading}
                isScheduleLoading={isScheduleLoading}
                canManage={canManage}
                invoiceUploadProps={invoiceUploadProps}
                companyUploadProps={companyUploadProps}
                isImportingInvoices={importInvoicesMutation.isPending}
                isImportingCompanies={importCompaniesMutation.isPending}
                isApproving={approveImportMutation.isPending}
                isRunning={runNowMutation.isPending}
                onApprove={() => approveImportMutation.mutate()}
                onRunNow={() => runNowMutation.mutate()}
                onRefresh={refreshAll}
                onEditFromError={handleEditFromError}
              />
            ),
          },
          {
            key: 'clients',
            label: 'Clienti',
            children: (
              <ClientiTab
                searchTerm={searchTerm}
                onSearchChange={(value) => {
                  setSearchTerm(value);
                  setPage(1);
                }}
                categoryFilter={categoryFilter}
                onCategoryFilterChange={(value) => {
                  setCategoryFilter(value);
                  setPage(1);
                }}
                emailFilterMode={emailFilterMode}
                onEmailFilterModeChange={(value) => {
                  setEmailFilterMode(value);
                  setPage(1);
                }}
                hasOutstandingOnly={hasOutstandingOnly}
                onHasOutstandingChange={(value) => {
                  setHasOutstandingOnly(value);
                  setPage(1);
                }}
                notificationsEnabledOnly={notificationsEnabledOnly}
                onNotificationsEnabledChange={(value) => {
                  setNotificationsEnabledOnly(value);
                  setPage(1);
                }}
                onRefresh={() => refetchClients()}
                onOpenBulkEdit={() => {
                  bulkForm.resetFields();
                  setBulkModalOpen(true);
                }}
                onRunBulkNotification={handleRunBulkNotification}
                selectedRowKeys={selectedRowKeys}
                setSelectedRowKeys={setSelectedRowKeys}
                clients={clients}
                totalItems={totalItems}
                isLoading={isClientsLoading}
                isError={isClientsError}
                errorMessage={(clientsError as any)?.message}
                page={page}
                pageSize={pageSize}
                onPageChange={(nextPage, nextPageSize) => {
                  setPage(nextPage);
                  setPageSize(nextPageSize || pageSize);
                }}
                onEditClient={openEditModal}
                onPreviewClientNotification={handlePreviewNotificationForClient}
                onPreviewClientNotificationPdf={handlePreviewNotificationPdfForClient}
                isPreviewingClientNotificationId={
                  previewClientNotificationMutation.isPending
                    ? ((previewClientNotificationMutation.variables as string) ?? null)
                    : null
                }
                isPreviewingClientNotificationPdfId={isPreviewingClientNotificationPdfId}
                isRunningClientNotificationId={
                  runClientNotificationMutation.isPending
                    ? ((runClientNotificationMutation.variables as string) ?? null)
                    : null
                }
                isRunningBulkNotification={runBulkClientNotificationMutation.isPending}
                summaryByCui={summaryByCui}
                isClientSummaryLoading={isClientSummaryLoading}
                toNumber={toNumber}
              />
            ),
          },
          {
            key: 'config',
            label: 'Configurare notificari',
            children: (
              <NotificationConfigTab
                categorySettings={categorySettings}
                clients={configClients}
                invoiceGroups={configInvoiceRows}
                isLoading={configLoading}
                isError={isConfigClientsError || isConfigInvoicesError}
                errorMessage={configErrorMessage}
                isSaving={saveNotificationConfigMutation.isPending}
                onSaveChanges={async (payload) => {
                  await saveNotificationConfigMutation.mutateAsync(payload);
                }}
                onOpenEditClient={openEditModal}
                onRefresh={() => {
                  refetchConfigClients();
                  refetchConfigInvoices();
                  refetchCategorySettings();
                  refetchPreflight();
                }}
                toNumber={toNumber}
              />
            ),
          },
          {
            key: 'notifications',
            label: 'Istoric notificari',
            children: (
              <NotificationTab
                dateRange={historyDateRange}
                onDateRangeChange={(range) => {
                  setHistoryDateRange(range);
                  setHistoryPage(1);
                }}
                clientName={historyClientName}
                onClientNameChange={(value) => {
                  setHistoryClientName(value);
                  setHistoryPage(1);
                }}
                cui={historyCui}
                onCuiChange={(value) => {
                  setHistoryCui(value);
                  setHistoryPage(1);
                }}
                onRefresh={() => refetchHistory()}
                isLoading={isHistoryLoading}
                isError={isHistoryError}
                errorMessage={(historyError as any)?.message}
                historyData={historyRows}
                historyTotalItems={historyTotalItems}
                historyPage={historyPage}
                historyPageSize={historyPageSize}
                onHistoryPageChange={(nextPage, nextPageSize) => {
                  setHistoryPage(nextPage);
                  setHistoryPageSize(nextPageSize || historyPageSize);
                }}
                categorySettings={categorySettings}
                isUpdatingCategory={updateCategorySettingsMutation.isPending || isCategorySettingsLoading}
                onCategoryToggle={handleToggleCategory}
                toNumber={toNumber}
              />
            ),
          },
        ]}
      />

      <Modal
        title='Editeaza client'
        open={editModalOpen}
        onCancel={closeEditModal}
        onOk={handleSaveClient}
        okText='Salveaza'
        confirmLoading={updateClientMutation.isPending}
      >
        <Form form={form} layout='vertical'>
          <Form.Item label='CUI'>
            <Input value={editingClient?.cui || ''} disabled />
          </Form.Item>
          <Form.Item name='name' label='Nume client'>
            <Input />
          </Form.Item>
          <Form.Item name='category' label='Categorie'>
            <Select options={categoryOptions} />
          </Form.Item>
          <Form.Item
            name='registrationNumber'
            label='Numar registru comert'
            rules={[
              {
                validator: (_rule, value: string | undefined) => {
                  const normalized = value?.trim();
                  if (!normalized) {
                    return Promise.resolve();
                  }

                  if (registrationNumberRegex.test(normalized)) {
                    return Promise.resolve();
                  }

                  return Promise.reject(new Error('Format invalid pentru numarul de registru al comertului.'));
                },
              },
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item name='email' label='To (email client)'>
            <Input placeholder='email1@domeniu.ro, email2@domeniu.ro' />
          </Form.Item>
          <Form.Item name='billingEmail' label='BCC (copie la noi)'>
            <Input placeholder='email1@domeniu.ro, email2@domeniu.ro' />
          </Form.Item>
          <Form.Item name='phone' label='Telefon'>
            <Input />
          </Form.Item>
          <Form.Item name='isEmailNotificationsEnabled' valuePropName='checked'>
            <Checkbox>Trimite notificari</Checkbox>
          </Form.Item>
          <Form.Item name='needsInfo' valuePropName='checked'>
            <Checkbox>Necesita info</Checkbox>
          </Form.Item>
          {notificationsEnabled && (
            <>
              <Form.Item name='attachPaymentNotificationPdf' valuePropName='checked'>
                <Checkbox>Ataseaza PDF notificare de plata</Checkbox>
              </Form.Item>
              {editAttachPaymentNotificationPdf && (
                <Form.Item name='attachPaymentNotificationPdfMode' label='Mod trimitere PDF notificare'>
                  <Select options={attachPdfModeOptions} />
                </Form.Item>
              )}
            </>
          )}
        </Form>
      </Modal>

      <Modal
        title='Editare multipla clienti'
        open={bulkModalOpen}
        onCancel={() => {
          setBulkModalOpen(false);
          bulkForm.resetFields();
        }}
        onOk={handleBulkSave}
        okText='Salveaza'
        confirmLoading={bulkUpdateMutation.isPending}
      >
        <Form form={bulkForm} layout='vertical'>
          <Alert
            type='info'
            showIcon
            style={{ marginBottom: 12 }}
            message='Completeaza doar campurile pe care vrei sa le modifici.'
            description='Campurile lasate goale sau pe "Nu modifica" raman neschimbate pentru clientii selectati.'
          />

          <div style={{ marginBottom: 12 }}>
            <Space wrap>
              <span>Campuri care vor fi modificate:</span>
              {bulkChangedFields.length > 0 ? (
                bulkChangedFields.map((field) => (
                  <Tag color='blue' key={field.key}>
                    {field.label}
                  </Tag>
                ))
              ) : (
                <Tag>Niciun camp selectat</Tag>
              )}
            </Space>
          </div>

          <Form.Item
            name='email'
            label='To (email client)'
            style={{ background: bulkChangedFields.some((f) => f.key === 'email') ? '#f0f9ff' : undefined, padding: 8 }}
          >
            <Input placeholder='email1@domeniu.ro, email2@domeniu.ro' />
          </Form.Item>
          <Form.Item
            name='billingEmail'
            label='BCC (copie la noi)'
            style={{
              background: bulkChangedFields.some((f) => f.key === 'billingEmail') ? '#f0f9ff' : undefined,
              padding: 8,
            }}
          >
            <Input placeholder='email1@domeniu.ro, email2@domeniu.ro' />
          </Form.Item>
          <Form.Item
            name='phone'
            label='Telefon'
            style={{ background: bulkChangedFields.some((f) => f.key === 'phone') ? '#f0f9ff' : undefined, padding: 8 }}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name='category'
            label='Categorie'
            style={{
              background: bulkChangedFields.some((f) => f.key === 'category') ? '#f0f9ff' : undefined,
              padding: 8,
            }}
          >
            <Select allowClear placeholder='Nu modifica' options={categoryOptions} />
          </Form.Item>
          <Form.Item
            name='isEmailNotificationsEnabled'
            label='Trimite notificari'
            style={{
              background: bulkChangedFields.some((f) => f.key === 'isEmailNotificationsEnabled')
                ? '#f0f9ff'
                : undefined,
              padding: 8,
            }}
          >
            <Select
              allowClear
              placeholder='Nu modifica'
              options={[
                { label: 'Pornite', value: true },
                { label: 'Oprite', value: false },
              ]}
            />
          </Form.Item>
          <Form.Item
            name='needsInfo'
            label='Necesita info'
            style={{
              background: bulkChangedFields.some((f) => f.key === 'needsInfo') ? '#f0f9ff' : undefined,
              padding: 8,
            }}
          >
            <Select
              allowClear
              placeholder='Nu modifica'
              options={[
                { label: 'Da', value: true },
                { label: 'Nu', value: false },
              ]}
            />
          </Form.Item>
          {(bulkNotificationsEnabled === true || bulkNotificationsEnabled === undefined) && (
            <>
              <Form.Item
                name='attachPaymentNotificationPdf'
                label='Ataseaza PDF notificare de plata'
                style={{
                  background: bulkChangedFields.some((f) => f.key === 'attachPaymentNotificationPdf')
                    ? '#f0f9ff'
                    : undefined,
                  padding: 8,
                }}
              >
                <Select
                  allowClear
                  placeholder='Nu modifica'
                  options={[
                    { label: 'Da', value: true },
                    { label: 'Nu', value: false },
                  ]}
                />
              </Form.Item>
              {(bulkAttachPaymentNotificationPdf === true || bulkAttachPaymentNotificationPdf === undefined) && (
                <Form.Item
                  name='attachPaymentNotificationPdfMode'
                  label='Mod trimitere PDF notificare'
                  style={{
                    background: bulkChangedFields.some((f) => f.key === 'attachPaymentNotificationPdfMode')
                      ? '#f0f9ff'
                      : undefined,
                    padding: 8,
                  }}
                >
                  <Select allowClear placeholder='Nu modifica' options={attachPdfModeOptions} />
                </Form.Item>
              )}
            </>
          )}
        </Form>
      </Modal>
    </div>
  );
};

export default FacturiScadente;
